<template>
  <div>
    <!-- Initial Loading Overlay -->
    <transition name="fade">
      <div
        v-if="isInitializing"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-md p-4"
      >
        <div class="boxComponent p-8 rounded-3xl max-w-md w-full text-center flex flex-col items-center bg-white shadow-xl border border-gray-100">
          <div class="w-16 h-16 mb-4 flex items-center justify-center">
            <svg
              class="w-12 h-12 text-primary animate-spin"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="#E5E7EB"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-2">Vérification du compte</h3>
          <p class="text-sm text-gray-600 mb-2">
            {{ initMessage }}
          </p>
          <span class="text-xs text-primary font-medium">Envoi de la demande en cours...</span>
        </div>
      </div>
    </transition>

    <!-- Main Verification Dialog -->
    <transition name="fade">
      <div
        v-if="dialogVisible"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-md p-4 overflow-y-auto"
        @click.self="handleBackdropClick"
      >
        <transition name="scale">
          <div
            class="boxComponent bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden my-6 border border-gray-100"
          >
            <!-- Success View -->
            <div v-if="isSuccess" class="p-8 text-center">
              <div class="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                <svg
                  class="w-12 h-12 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 class="text-2xl font-bold text-gray-900 mb-2">
                {{ isAlreadyVerified ? 'Compte déjà vérifié !' : 'Compte vérifié avec succès !' }}
              </h2>
              <p class="text-gray-600 mb-6 text-sm">
                {{ isAlreadyVerified 
                  ? 'Ce compte Mobile Money est déjà enregistré et validé.' 
                  : 'Votre compte Mobile Money a été validé avec succès.' 
                }}
              </p>
            </div>

            <!-- Code Entry & Instructions View -->
            <div v-else class="p-6 sm:p-8 bg-white">
              <!-- Header -->
              <div class="flex items-center justify-between pb-4 border-b border-gray-100">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="w-6 h-6">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-bold text-gray-900">Validation du compte</h3>
                    <p class="text-xs text-gray-500">Vérification Mobile Money</p>
                  </div>
                </div>
                <button
                  @click="cancelVerification"
                  class="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Explanatory Notice -->
              <div class="mt-4 p-4 rounded-2xl bg-amber-50 border border-amber-200">
                <div class="flex items-start gap-3">
                  <span class="text-amber-600 text-lg">💡</span>
                  <div class="text-xs sm:text-sm text-amber-900 leading-relaxed">
                    <p class="font-semibold mb-1">
                      Une demande de paiement de <span class="text-amber-700 font-bold">1 FCFA</span> a été envoyée au 
                      <span class="font-bold underline">{{ accountData.phoneNumber }}</span> ({{ accountData.operator }}).
                    </p>
                    <ol class="list-decimal list-inside space-y-1 mt-1 text-xs text-amber-800">
                      <li>Confirmez le débit de 1 FCFA sur votre téléphone avec votre code secret Mobile Money.</li>
                      <li>Repérez l'<strong>ID de transaction</strong> dans le SMS de confirmation reçu.</li>
                      <li>Saisissez cet ID ci-dessous pour valider votre compte.</li>
                    </ol>
                  </div>
                </div>
              </div>

              <!-- SMS Demo Image -->
              <div class="my-4 text-center">
                <p class="text-xs font-medium text-gray-500 mb-2">Exemple de SMS reçu :</p>
                <div class="inline-block p-1 bg-gray-50 rounded-2xl border border-gray-200 shadow-sm max-w-full">
                  <img
                    :src="smsDemoImage"
                    alt="Exemple SMS de validation"
                    class="rounded-xl max-h-36 sm:max-h-44 object-contain mx-auto"
                  />
                </div>
                <p class="text-[11px] text-gray-400 mt-1 italic">
                  Copiez le code entouré en rouge après "ID"
                </p>
              </div>

              <!-- Status Indicator -->
              <div class="mb-4">
                <div
                  v-if="operationStatus === 'WAITING_CODE'"
                  class="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-50 text-green-700 text-xs font-medium border border-green-200"
                >
                  <span class="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
                  <span>Prêt pour la validation : entrez votre code de confirmation SMS</span>
                </div>
                <div
                  v-else-if="operationStatus === 'FAILED' || operationStatus === 'ABORTED'"
                  class="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 text-red-700 text-xs font-medium border border-red-200"
                >
                  <span>⚠️ Échec de la vérification du compte</span>
                </div>
                <div
                  v-else
                  class="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200"
                >
                  <svg class="w-3.5 h-3.5 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                  </svg>
                  <span>En attente de la confirmation... Vous pouvez déjà saisir votre code ci-dessous.</span>
                </div>
              </div>

              <!-- Error Alert -->
              <div
                v-if="errorMessage"
                class="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-xs font-medium border border-red-200 flex items-center justify-between"
              >
                <span>{{ errorMessage }}</span>
                <button @click="errorMessage = ''" class="text-red-500 hover:text-red-700 font-bold ml-2">✕</button>
              </div>

              <!-- Input Code Field -->
              <div class="mb-6">
                <label class="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  ID de Transaction / Code de confirmation
                </label>
                <div class="relative">
                  <input
                    type="text"
                    v-model="verificationCode"
                    placeholder="Ex: 12810296759"
                    @keyup.enter="handleCodeSubmit"
                    class="w-full px-4 py-3.5 rounded-xl border border-gray-300 bg-gray-50 text-gray-900 font-mono text-base focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                  />
                  <button
                    v-if="verificationCode"
                    @click="verificationCode = ''"
                    type="button"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs px-2 py-1 rounded"
                  >
                    Effacer
                  </button>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  @click="cancelVerification"
                  class="w-full sm:w-1/3 py-3.5 px-4 rounded-xl border border-gray-300 text-gray-600 font-medium hover:bg-gray-50 transition-colors text-sm text-center"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  @click="handleCodeSubmit"
                  :disabled="!verificationCode.trim() || isSubmitting"
                  class="w-full sm:w-2/3 py-3.5 px-6 rounded-xl bg-primary hover:bg-primary-600 text-white font-semibold shadow-lg shadow-primary/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98] text-sm flex items-center justify-center gap-2"
                >
                  <svg
                    v-if="isSubmitting"
                    class="w-4 h-4 animate-spin text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                  </svg>
                  <span>{{ isSubmitting ? 'Validation en cours...' : 'Valider le compte' }}</span>
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import smsDemoImg from '@/presentation/assets/images/withdrawal/mobile-account-validation.png'

const FEDAPAY_PILOT_BASE_URL = 'https://api.monrapidoo.com/fedapay-pilot'
const DEFAULT_GROUP = 'ultraxeno'

export default defineComponent({
  name: 'AddFedapayWithdrawalAccount',
  emits: ['verified', 'cancelled', 'error'],
  data() {
    return {
      isInitializing: false,
      dialogVisible: false,
      isSubmitting: false,
      isSuccess: false,
      isAlreadyVerified: false,
      initMessage: 'Vérification du compte en cours...',
      errorMessage: '',
      accountData: {},
      operationId: null,
      operationStatus: 'PENDING',
      verificationCode: '',
      pollIntervalTimer: null,
      initTimeoutTimer: null,
      waitingToSubmitCode: false,
      smsDemoImage: smsDemoImg,
    }
  },
  beforeUnmount() {
    this.stopPolling()
    this.clearInitTimeout()
  },
  methods: {
    showToast(type, title, body) {
      if (this.$Commons?.toastNotification) {
        this.$Commons.toastNotification(type === 'error' ? 'danger' : type, body || title)
      }
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        type: type || 'error',
        title: title || 'Information',
        body: body || title || '',
      })
    },

    async startVerification(data) {
      this.accountData = { ...data }
      this.verificationCode = ''
      this.errorMessage = ''
      this.isSuccess = false
      this.isAlreadyVerified = false
      this.isSubmitting = false
      this.waitingToSubmitCode = false
      this.operationStatus = 'PENDING'
      this.initMessage = 'Initialisation de la demande de vérification...'
      this.isInitializing = true
      this.dialogVisible = false

      try {
        const ownerName = String(
          this.accountData.accountOwnerName ||
          this.accountData.owner ||
          this.accountData.accountLabel ||
          this.accountData.name ||
          this.accountData.fullName ||
          (this.accountData.firstName ? `${this.accountData.firstName} ${this.accountData.lastName || ''}`.trim() : '') ||
          'Compte MonRapidoo'
        ).trim()

        const addRes = await this.apiAddAccount({
          accountOwnerName: ownerName,
          operator: this.accountData.operator,
          country: this.accountData.country || 'Bénin',
          phoneNumber: this.accountData.rawPhoneNumber || this.accountData.phoneNumber,
        })

        if (addRes.data?.result?.alreadyAdded || addRes.data?.result?.isVerified || addRes.data?.isVerified) {
          this.isInitializing = false
          this.isAlreadyVerified = true
          this.onVerificationSuccessful()
          return
        }

        if (!addRes.success || !addRes.data?.operationId) {
          this.isInitializing = false
          const errMsg = addRes.message || "Impossible d'initier la vérification du compte"
          this.showToast('error', 'Erreur de vérification', errMsg)
          this.$emit('error', addRes)
          return
        }

        this.operationId = addRes.data.operationId
        this.operationStatus = addRes.data.status || 'PENDING'

        this.startPolling()

        // Secours par Timeout de 15 secondes
        this.clearInitTimeout()
        this.initTimeoutTimer = setTimeout(() => {
          if (this.isInitializing && !this.isSuccess) {
            this.isInitializing = false
            this.dialogVisible = true
          }
        }, 15000)
      } catch (err) {
        this.isInitializing = false
        console.error('Account verification start error:', err)
        const errMsg = err.message || "Erreur lors de l'initialisation de la vérification"
        this.showToast('error', 'Erreur de vérification', errMsg)
        this.$emit('error', err)
      }
    },

    startPolling() {
      this.stopPolling()
      this.pollIntervalTimer = setInterval(async () => {
        if (!this.operationId) return

        const opRes = await this.apiGetOperation(this.operationId)
        if (opRes.success && opRes.data) {
          const newStatus = opRes.data.status || this.operationStatus
          this.operationStatus = newStatus

          if (opRes.data.result?.alreadyAdded || (newStatus === 'SUCCESS' && opRes.data.result?.isVerified)) {
            this.stopPolling()
            this.clearInitTimeout()
            this.isInitializing = false
            this.isAlreadyVerified = !!opRes.data.result?.alreadyAdded
            this.onVerificationSuccessful()
            return
          }

          if (newStatus === 'WAITING_CODE') {
            if (this.isInitializing) {
              this.clearInitTimeout()
              this.isInitializing = false
              this.dialogVisible = true
            }

            if (this.waitingToSubmitCode && this.verificationCode.trim()) {
              this.waitingToSubmitCode = false
              this.submitCodeToFedapay()
            }
          }

          if (newStatus === 'SUCCESS') {
            this.stopPolling()
            this.clearInitTimeout()
            this.isInitializing = false
            this.onVerificationSuccessful()
          } else if (newStatus === 'FAILED' || newStatus === 'ABORTED') {
            this.isSubmitting = false
            this.waitingToSubmitCode = false
            const failureReason =
              opRes.data.error?.message ||
              opRes.data.result?.message ||
              opRes.message ||
              "La vérification du compte a échoué. Veuillez recommencer."
            this.errorMessage = failureReason
            if (this.isInitializing) {
              this.clearInitTimeout()
              this.isInitializing = false
              this.dialogVisible = true
            }
          }
        }
      }, 2000)
    },

    stopPolling() {
      if (this.pollIntervalTimer) {
        clearInterval(this.pollIntervalTimer)
        this.pollIntervalTimer = null
      }
    },

    clearInitTimeout() {
      if (this.initTimeoutTimer) {
        clearTimeout(this.initTimeoutTimer)
        this.initTimeoutTimer = null
      }
    },

    async handleCodeSubmit() {
      const code = this.verificationCode.trim()
      if (!code) {
        this.errorMessage = "Veuillez saisir l'ID / code de confirmation reçu par SMS."
        return
      }

      this.errorMessage = ''
      this.isSubmitting = true

      if (this.operationStatus !== 'WAITING_CODE' && this.operationStatus !== 'SUCCESS') {
        this.waitingToSubmitCode = true
        return
      }

      await this.submitCodeToFedapay()
    },

    async submitCodeToFedapay() {
      this.isSubmitting = true
      this.errorMessage = ''

      try {
        const verifyRes = await this.apiVerifyCode({
          operationId: this.operationId,
          code: this.verificationCode.trim(),
        })

        if (verifyRes.success) {
          this.stopPolling()
          this.clearInitTimeout()
          this.onVerificationSuccessful()
        } else {
          // Pas de retry : fermer immédiatement et notifier l'échec
          this.stopPolling()
          this.clearInitTimeout()
          this.isSubmitting = false
          this.dialogVisible = false
          this.isInitializing = false

          const errMsg =
            verifyRes.message ||
            "Code de confirmation incorrect. Veuillez recommencer l'opération depuis le début."

          this.showToast('error', 'Erreur de vérification', errMsg)
          this.$emit('error', { message: errMsg, verifyRes })
        }
      } catch (err) {
        // Pas de retry : fermer immédiatement et notifier l'échec
        this.stopPolling()
        this.clearInitTimeout()
        this.isSubmitting = false
        this.dialogVisible = false
        this.isInitializing = false

        const errMsg =
          err.message ||
          "Une erreur est survenue lors de la validation du code. Veuillez recommencer."
        this.showToast('error', 'Erreur de vérification', errMsg)
        this.$emit('error', { message: errMsg, error: err })
      }
    },

    onVerificationSuccessful() {
      this.isSuccess = true
      this.isSubmitting = false
      this.waitingToSubmitCode = false
      this.dialogVisible = true

      setTimeout(() => {
        this.dialogVisible = false
        this.isSuccess = false
        this.isAlreadyVerified = false
        this.$emit('verified', this.accountData)
      }, 1500)
    },

    cancelVerification() {
      this.stopPolling()
      this.clearInitTimeout()
      this.dialogVisible = false
      this.isInitializing = false
      this.isSubmitting = false
      this.waitingToSubmitCode = false
      this.$emit('cancelled')
    },

    handleBackdropClick() {
      if (this.isSubmitting) return
      this.cancelVerification()
    },

    async apiAddAccount({ accountOwnerName, operator = 'mtn', country = 'Bénin', phoneNumber }) {
      try {
        let cleanPhone = String(phoneNumber).replace(/\+/g, '').replace(/\s+/g, '').trim()
        if (cleanPhone.startsWith('229') && cleanPhone.length > 8) {
          cleanPhone = cleanPhone.slice(3)
        }

        const ownerName = String(
          accountOwnerName ||
          this.accountData?.accountOwnerName ||
          this.accountData?.owner ||
          this.accountData?.accountLabel ||
          this.accountData?.name ||
          this.accountData?.fullName ||
          (this.accountData?.firstName ? `${this.accountData.firstName} ${this.accountData.lastName || ''}`.trim() : '') ||
          'Compte MonRapidoo'
        ).trim()

        const url = `${FEDAPAY_PILOT_BASE_URL}/account/add?group=${DEFAULT_GROUP}`
        const body = {
          accountOwnerName: ownerName,
          operator: (operator || 'mtn').toLowerCase(),
          country: country || 'Bénin',
          phoneNumber: cleanPhone,
        }

        const response = await axios.post(url, body, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000,
        })

        return {
          success: response.status >= 200 && response.status < 300,
          statusCode: response.status,
          data: response.data?.data || null,
          message: response.data?.message || '',
        }
      } catch (error) {
        const responseData = error?.response?.data
        return {
          success: false,
          statusCode: error?.response?.status || 500,
          data: responseData?.data || null,
          message: responseData?.message || error.message || "Erreur lors de l'initialisation de la vérification",
          error: responseData || error,
        }
      }
    },

    async apiGetOperation(operationId) {
      try {
        const url = `${FEDAPAY_PILOT_BASE_URL}/operation/${operationId}?group=${DEFAULT_GROUP}`
        const response = await axios.get(url, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
        })

        return {
          success: response.status >= 200 && response.status < 300,
          statusCode: response.status,
          data: response.data?.data || null,
          message: response.data?.message || '',
        }
      } catch (error) {
        const responseData = error?.response?.data
        return {
          success: false,
          statusCode: error?.response?.status || 500,
          data: responseData?.data || null,
          message: responseData?.message || error.message || "Erreur lors de la récupération de l'opération",
          error: responseData || error,
        }
      }
    },

    async apiVerifyCode({ operationId, code }) {
      try {
        const url = `${FEDAPAY_PILOT_BASE_URL}/account/verify-code?group=${DEFAULT_GROUP}`
        const body = {
          operationId,
          code: String(code).trim(),
        }

        const response = await axios.post(url, body, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 45000,
        })

        const data = response.data?.data || {}
        const isSuccess = response.status === 200 && (data.status === 'SUCCESS' || data.isVerified === true)

        return {
          success: isSuccess,
          statusCode: response.status,
          data,
          message: response.data?.message || 'Vérification réussie',
        }
      } catch (error) {
        const responseData = error?.response?.data
        return {
          success: false,
          statusCode: error?.response?.status || 400,
          data: responseData?.data || null,
          message: responseData?.message || error.message || 'Code de confirmation invalide ou expiré',
          error: responseData || error,
        }
      }
    },
  },
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-enter-active,
.scale-leave-active {
  transition: all 0.25s ease;
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>